import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  ArrowLeft,
  Hash,
  AlertCircle,
  Loader2,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AnalyticsFreshnessBanner } from "@/components/admin/shared/AnalyticsFreshnessBanner";
import { analyticsApi } from "@/lib/admin-api";
import type { SearchQueryEntry } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Period options
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS = [
  { label: "7 jours", value: 7 },
  { label: "30 jours", value: 30 },
  { label: "90 jours", value: 90 },
] as const;

type FilterTab = "all" | "trending" | "zero";

const FILTER_TABS: readonly { readonly label: string; readonly value: FilterTab }[] = [
  { label: "Toutes", value: "all" },
  { label: "Tendances", value: "trending" },
  { label: "Sans r\u00e9sultat", value: "zero" },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  accent,
  icon: Icon,
}: {
  readonly label: string;
  readonly value: string | number;
  readonly accent?: boolean;
  readonly icon: React.ElementType;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <p
        className={`text-2xl font-bold ${accent ? "text-red-400" : "text-foreground"}`}
      >
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SearchAnalytics() {
  const [days, setDays] = useState<number>(7);
  const [activeTab, setActiveTab] = useState<FilterTab>("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["analytics", "search-queries", days],
    queryFn: () => analyticsApi.searchQueries(days),
  });

  // Build a set of zero-result query strings for fast lookup
  const zeroResultSet = useMemo(() => {
    if (!data?.data.zero_result_queries) return new Set<string>();
    return new Set(data.data.zero_result_queries.map((q) => q.query));
  }, [data]);

  // Derive unique terms count
  const uniqueTerms = useMemo(() => {
    if (!data?.data.queries) return 0;
    return new Set(data.data.queries.map((q) => q.query)).size;
  }, [data]);

  // Filtered rows based on active tab
  const filteredRows: readonly SearchQueryEntry[] = useMemo(() => {
    if (!data) return [];
    const allQueries = data.data.queries;
    switch (activeTab) {
      case "zero":
        return data.data.zero_result_queries;
      case "trending":
        return [...allQueries].sort(
          (a, b) => b.total_searches - a.total_searches,
        );
      default:
        return allQueries;
    }
  }, [data, activeTab]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AnalyticsFreshnessBanner />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/analytics"
              className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">
                Recherches
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Analyse des recherches utilisateurs
              </p>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  days === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading / Error */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-muted-foreground" size={28} />
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center">
            <AlertCircle className="mx-auto mb-2 text-destructive" size={24} />
            <p className="text-sm text-destructive">
              Impossible de charger les données de recherche.
            </p>
          </div>
        )}

        {data && (
          <>
            {/* Summary stat cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard
                label="Total recherches"
                value={data.data.total_searches.toLocaleString("fr-FR")}
                icon={Search}
              />
              <StatCard
                label="Termes uniques"
                value={uniqueTerms.toLocaleString("fr-FR")}
                icon={Hash}
              />
              <StatCard
                label="Sans résultat"
                value={data.data.zero_result_count.toLocaleString("fr-FR")}
                accent
                icon={AlertCircle}
              />
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-1 border-b border-border">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab.value
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Terme recherché
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Recherches
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Résultats moy.
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Clics
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Taux conversion
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-muted-foreground"
                        >
                          Aucune donnée pour cette période.
                        </td>
                      </tr>
                    )}
                    {filteredRows.map((row) => {
                      const isZeroResult = zeroResultSet.has(row.query);
                      return (
                        <tr
                          key={row.query}
                          className={`border-b border-border last:border-0 transition-colors ${
                            isZeroResult
                              ? "bg-red-500/10 hover:bg-red-500/15"
                              : "hover:bg-muted/20"
                          }`}
                        >
                          <td className="px-4 py-3 font-medium text-foreground">
                            {row.query}
                            {isZeroResult && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-400">
                                0 résultat
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-foreground tabular-nums">
                            {row.total_searches.toLocaleString("fr-FR")}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">
                            {row.avg_results_count != null
                              ? (row.avg_results_count ?? 0).toFixed(1)
                              : "\u2014"}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">
                            {formatRate(row.click_through_rate)}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">
                            {formatRate(row.conversion_rate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
